import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Field from 'components/Recommendations/Campaign/Edit/CampaignForm/Field'
import SearchDropdownMultiSelect from 'components/Recommendations/Campaign/Edit/SearchDropdownMultiSelect'
import { getErrorText } from 'components/Recommendations/Campaign/Edit/adAccountHelper'
import { arrayReducer, initStateArray } from 'reducers/default'
import { AssetClass, Platform, tcDisplayName } from 'api/models'
import { fetchAudiences } from 'api/integrations'
import { useCampaignSetup } from 'components/Recommendations/Campaign/Context/CampaignSetupContext'

const EXCLUDED_AUDIENCES_ENABLED = false

export function MetaAudienceTargetingField({
  tcTargeting,
  onChange,
  disabled,
  event,
  bothSelected,
}) {
  const { t } = useTranslation()
  const { setup } = useCampaignSetup()
  const [customAudiences, dispatchCustomAudiencesAction] = useReducer(arrayReducer, initStateArray)
  const [savedAudiences, dispatchSavedAudiencesAction] = useReducer(arrayReducer, initStateArray)
  const [isAudiencesFetched, setIsAudiencesFetched] = useState(false)

  // Gate on a genuinely-connected Meta ad account, not the (unreliable —
  // defaults to "both" whenever no TC has fb/google targeting content yet)
  // activeTargetingPlatforms(derivePlatformSelectionFromSetup(setup))
  // inference — see GoogleAudienceTargetingField's identical guard.
  const adAccountId = useMemo(
    () =>
      (setup.integration_details || []).find(
        (x) => x.asset_class === AssetClass.ad_account && x.platform === Platform.facebook,
      )?.asset_id,
    [setup.integration_details],
  )

  const hasAudienceLimitations = useMemo(() => event?.special_ad_category?.length > 0, [event])

  const retrieveAudiences = useCallback(() => {
    if (!isAudiencesFetched && adAccountId) {
      fetchAudiences(dispatchCustomAudiencesAction, adAccountId, 'custom')
      fetchAudiences(dispatchSavedAudiencesAction, adAccountId, 'saved')
      setIsAudiencesFetched(true)
    }
  }, [adAccountId, isAudiencesFetched])

  useEffect(() => {
    retrieveAudiences()
  }, [retrieveAudiences])

  const customAudienceIds = useMemo(
    () => (tcTargeting.fb?.custom_audiences ?? []).map((x) => x.id),
    [tcTargeting.fb],
  )
  const savedAudienceId = tcTargeting.fb?.saved_audience_id ?? null
  const excludedAudienceIds = useMemo(
    () => (tcTargeting.fb?.excluded_custom_audiences ?? []).map((x) => x.id),
    [tcTargeting.fb],
  )

  const value = savedAudienceId || (customAudienceIds.length ? customAudienceIds.join(',') : null)
  const excludeValue = excludedAudienceIds.length ? excludedAudienceIds.join(',') : null

  const emitFb = useCallback(
    (fbChanges) => {
      const fb = { ...(tcTargeting.fb || {}), ...fbChanges }
      Object.keys(fb).forEach((key) => {
        if (fb[key] === undefined) {
          delete fb[key]
        }
      })
      onChange({ fb })
    },
    [onChange, tcTargeting.fb],
  )

  const handleChange = (idsString) => {
    if (!idsString) {
      emitFb({ custom_audiences: [], saved_audience_id: undefined })
      return
    }
    const ids = idsString.split(',').filter(Boolean)
    const isSavedAudience = ids.length === 1 && savedAudiences.items?.some((a) => a.id === ids[0])
    if (isSavedAudience) {
      emitFb({ custom_audiences: [], saved_audience_id: ids[0] })
    } else {
      emitFb({ custom_audiences: ids.map((id) => ({ id })), saved_audience_id: undefined })
    }
  }

  const handleExcludeChange = (idsString) => {
    const ids = (idsString || '').split(',').filter(Boolean)
    emitFb({ excluded_custom_audiences: ids.map((id) => ({ id })) })
  }

  if (!adAccountId) {
    return null
  }

  return (
    <Field
      fieldName="metaAudience"
      as={SearchDropdownMultiSelect}
      tooltip={{ content: 'Tips.Recommendations.CreativeComposer.Audience' }}
      inputTooltip={{
        body: t('Tips.Recommendations.CreativeComposer.AudienceLimitations.body'),
        enabled: hasAudienceLimitations,
        withAnchor: false,
        placement: 'top',
      }}
      value={value}
      defaultValue={t('Recommendations.campaign.recommendationTitle', {
        tc: tcDisplayName(tcTargeting),
      })}
      label={'meta'}
      bothSelected={bothSelected}
      onChange={handleChange}
      isRequired={false}
      disabled={disabled || hasAudienceLimitations}
      options={customAudiences.items}
      savedAudiences={savedAudiences.items}
      error={{
        custom: customAudiences.error ? getErrorText(customAudiences.message) : null,
        saved: savedAudiences.error ? getErrorText(savedAudiences.message) : null,
      }}
      loading={{
        custom: customAudiences.loading,
        saved: savedAudiences.loading,
      }}
      onErrorBtnClick={retrieveAudiences}
      dropdownClasses="audience"
      extraTabs={[
        EXCLUDED_AUDIENCES_ENABLED
          ? {
              key: 'excluded_custom_audiences',
              title: t('Recommendations.campaign.setup.audience.excluded'),
              placeholder: t('Recommendations.campaign.setup.audience.placeholder.excluded'),
              options: customAudiences.items,
              value: excludeValue,
              onChange: handleExcludeChange,
              loading: customAudiences.loading,
              error: customAudiences.error ? getErrorText(customAudiences.message) : null,
              onErrorBtnClick: retrieveAudiences,
            }
          : {},
      ]}
    />
  )
}

MetaAudienceTargetingField.propTypes = {
  tcTargeting: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  event: PropTypes.object,
  bothSelected: PropTypes.bool,
}
